import type { CreateIncidentWitnessInput } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { uuid } from '@piccolohealth/util';
import React from 'react';
import { useFieldArray } from 'react-hook-form';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { FormStack } from './FormStack';
import { HookedFormField } from './HookedFormField';
import { HookedInput } from './HookedInput';

interface Props {
  name: string;
}

export const HookedWitnessesControl = (props: Props) => {
  const { name } = props;
  const { fields, append, remove } = useFieldArray({
    name,
  });

  const witnesses = fields as unknown as CreateIncidentWitnessInput[];

  const onAddWitness = React.useCallback(() => {
    append({
      id: uuid(),
      name: null,
      phone: null,
      email: null,
      description: null,
    });
  }, [append]);

  const onRemoveWitness = React.useCallback(
    (index: number) => {
      remove(index);
    },
    [remove],
  );

  return (
    <FormStack>
      {witnesses.map((witness, index) => {
        return (
          <>
            <ChakraV3.HStack key={`witness-${index}`}>
              <FormStack gap={4} pl={4} ml={1} borderLeftWidth='2px' borderColor='purple.400'>
                <FormStack orientation='horizontal'>
                  <HookedFormField
                    name={`${name}.${index}.name`}
                    label='Name'
                    helperText='The witnesses name'
                  >
                    <HookedInput
                      name={`${name}.${index}.name`}
                      size='sm'
                      placeholder='Enter a name'
                    />
                  </HookedFormField>
                  <HookedFormField
                    name={`${name}.${index}.email`}
                    label='Email'
                    helperText='The witnesses email'
                  >
                    <HookedInput
                      name={`${name}.${index}.email`}
                      size='sm'
                      placeholder='Enter an email'
                    />
                  </HookedFormField>
                </FormStack>
                <FormStack orientation='horizontal'>
                  <HookedFormField
                    name={`${name}.${index}.phone`}
                    label='Phone'
                    helperText='The witnesses phone'
                  >
                    <HookedInput
                      name={`${name}.${index}.phone`}
                      size='sm'
                      placeholder='Enter a phone number'
                    />
                  </HookedFormField>
                  <HookedFormField
                    name={`${name}.${index}.description`}
                    label='Description'
                    helperText='Who is the witness?'
                  >
                    <HookedInput
                      name={`${name}.${index}.description`}
                      size='sm'
                      placeholder='Enter a description'
                    />
                  </HookedFormField>
                </FormStack>
              </FormStack>
              <ChakraV3.Stack align='center' w='4'>
                <ChakraV3.IconButton
                  aria-label='Remove witness'
                  size='2xs'
                  variant='ghost'
                  onClick={() => onRemoveWitness(index)}
                >
                  <FaTrash />
                </ChakraV3.IconButton>
              </ChakraV3.Stack>
            </ChakraV3.HStack>
            {index !== witnesses.length - 1 && <ChakraV3.Separator />}
          </>
        );
      })}
      <ChakraV3.Button onClick={() => onAddWitness()} size='sm' variant='subtle'>
        <FaPlus />
        Add a witness
      </ChakraV3.Button>
    </FormStack>
  );
};
